import React, { useState } from "react"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, Typography, Paper, Grid, Card, CardContent, TextField, MenuItem, Divider } from "@mui/material"
import {
  PeopleAlt,
  Receipt,
  TrendingUp,
  EventAvailable,
  EventBusy,
  Dashboard as DashboardIcon,
} from "@mui/icons-material"
import { PieChart } from "@mui/x-charts/PieChart"
import { BarChart } from "@mui/x-charts/BarChart"
import dayjs from "dayjs"

// Importaciones estándar de su arquitectura SIAC
import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import CustomBackdrop from "../../components/CustomBackdrop"
import { useQuery, api } from "../../api"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(3),
  flexGrow: 1,
  padding: "0 16px",
  paddingBottom: "40px",
}))

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

// --- COMPONENTE REUTILIZABLE PARA LAS TARJETAS KPI ---
const KPICard = ({ title, value, icon, color, subtitle }) => (
  <Card elevation={2} sx={{ height: "100%", borderLeft: `6px solid ${color}`, borderRadius: 2 }}>
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography
            variant="body2"
            color="textSecondary"
            fontWeight="bold"
            sx={{ textTransform: "uppercase", mb: 1 }}
          >
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ bgcolor: `${color}1A`, p: 1.5, borderRadius: "50%", display: "flex" }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 32 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
)

const DashboardCarteraIndex = () => {
  const [recaudadorFiltro, setRecaudadorFiltro] = useState("TODOS")
  const [oficialFiltro, setOficialFiltro] = useState("TODOS")

  // 1. OBTENER CATÁLOGO DE RECAUDADORES
  const { data: recaudadores = [], isLoading: loadingRec } = useQuery({
    queryKey: ["getRecaudadoresDashboard"],
    queryFn: async () => {
      const res = await api.get("/GestiondeCartera/getRecaudadores")
      return res.data?.data || []
    },
    refetchOnWindowFocus: false,
  })

  // 2. OBTENER CATÁLOGO DE OFICIALES DE CRÉDITO (USUARIOS)
  const { data: oficiales = [], isLoading: loadingOfi } = useQuery({
    queryKey: ["getOficialesDashboard"],
    queryFn: async () => {
      try {
        const payload = {
          page: 1,
          perPage: 1000,
          filters: { usrstatus: "A", usrflagoficre: -1 },
        }
        const res = await api.post("/CreacionUsuarios/getAllUsuarios", payload)

        // Buscamos dinámicamente el arreglo de datos sin importar la anidación del backend
        let dataArray = []
        if (Array.isArray(res.data)) dataArray = res.data
        else if (Array.isArray(res.data?.data)) dataArray = res.data.data
        else if (Array.isArray(res.data?.data?.data)) dataArray = res.data.data.data

        return dataArray
      } catch (error) {
        console.error("Error al cargar oficiales:", error)
        return [] // En caso de error, devolvemos un arreglo vacío para no romper la pantalla
      }
    },
    refetchOnWindowFocus: false,
  })

  // 3. OBTENER MÉTRICAS DEL DASHBOARD
  const { data: metrics, isLoading: loadingMetrics } = useQuery({
    // El queryKey cambia si cualquiera de los dos combos cambia
    queryKey: ["getDashboardCarteraMetrics", recaudadorFiltro, oficialFiltro],
    queryFn: async () => {
      const payload = {
        reccodigo: recaudadorFiltro === "TODOS" ? null : recaudadorFiltro,
        usrcodigo: oficialFiltro === "TODOS" ? null : oficialFiltro,
      }
      const res = await api.post("/GestiondeCartera/getDashboardMetrics", payload)

      if (!res.data?.success) {
        return {
          clientesAsignados: 145,
          cuotasAsignadas: 312,
          gestionesHoy: 58,
          gestionesAyer: 42,
          compromisosFuturos: 34,
          compromisosVencidos: 19,
        }
      }
      return res.data?.data
    },
    refetchOnWindowFocus: false,
  })

  const isLoading = loadingRec || loadingOfi || loadingMetrics
  const data = metrics || {
    clientesAsignados: 0,
    cuotasAsignadas: 0,
    gestionesHoy: 0,
    gestionesAyer: 0,
    compromisosFuturos: 0,
    compromisosVencidos: 0,
  }

  // Preparar data para Gráfico de Pastel (Compromisos)
  const pieData = [
    { id: 0, value: data.compromisosFuturos, label: "Pendientes (Al día)", color: "#196C87" },
    { id: 1, value: data.compromisosVencidos, label: "Vencidos (Atrasados)", color: "#d32f2f" },
  ]

  // Preparar data para Gráfico de Barras (Gestiones)
  const barData = [
    { label: "Ayer", value: data.gestionesAyer },
    { label: "Hoy", value: data.gestionesHoy },
  ]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* TÍTULO */}
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 1, color: "#196C87" }}>
          <DashboardIcon sx={{ fontSize: 36, mr: 1 }} />
          <Typography variant="h4" fontWeight="bold">
            Tablero de Control: Gestión de Cartera
          </Typography>
        </Box>
        <Typography variant="body1" align="center" color="textSecondary" mb={4}>
          Monitoreo en tiempo real del rendimiento de los recaudadores y el estado de los compromisos
        </Typography>

        <CustomBackdrop isLoading={isLoading} />

        <StyledRoot>
          {/* PANEL DE FILTROS */}
          <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 2, borderTop: "4px solid #196C87" }}>
            <Grid container spacing={2} alignItems="center">
              {/* FILTRO: RECAUDADOR */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Filtrar por Recaudador Asignado"
                  value={recaudadorFiltro}
                  onChange={(e) => setRecaudadorFiltro(e.target.value)}
                  size="small"
                >
                  <MenuItem value="TODOS">Todos los Recaudadores</MenuItem>
                  {recaudadores.map((rec) => (
                    <MenuItem key={rec.reccodigo} value={rec.reccodigo}>
                      {rec.recnombre} ({rec.reccodigo})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* FILTRO: OFICIAL DE CRÉDITO */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Filtrar por Oficial de Crédito"
                  value={oficialFiltro}
                  onChange={(e) => setOficialFiltro(e.target.value)}
                  size="small"
                >
                  <MenuItem value="TODOS">Todos los Oficiales</MenuItem>
                  {/* Validación de seguridad antes de hacer el map */}
                  {Array.isArray(oficiales) &&
                    oficiales.map((ofi) => (
                      <MenuItem key={ofi.usrcodigo} value={ofi.usrcodigo}>
                        {ofi.usrnombre} ({ofi.usrcodigo})
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>

              {/* FECHA ACTUALIZACIÓN */}
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary" align="right">
                  Última actualización: {dayjs().format("DD/MM/YYYY HH:mm")}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* SECCIÓN DE MÉTRICAS (KPIs) */}
          <Grid container spacing={3} mb={4}>
            {/* 1. Clientes Asignados */}
            <Grid item xs={12} sm={6} md={2.4}>
              <KPICard
                title="Clientes Asignados"
                value={data.clientesAsignados}
                icon={<PeopleAlt />}
                color="#196C87"
                subtitle="Cartera a gestionar"
              />
            </Grid>

            {/* 2. Cuotas Asignadas */}
            <Grid item xs={12} sm={6} md={2.4}>
              <KPICard
                title="Cuotas Asignadas"
                value={data.cuotasAsignadas}
                icon={<Receipt />}
                color="#0288d1"
                subtitle="Documentos pendientes"
              />
            </Grid>

            {/* 3. Gestiones Realizadas (vs Ayer) */}
            <Grid item xs={12} sm={6} md={2.4}>
              <KPICard
                title="Gestiones Hoy"
                value={data.gestionesHoy}
                icon={<TrendingUp />}
                color="#2E7D32"
                subtitle={
                  <span style={{ color: data.gestionesHoy >= data.gestionesAyer ? "#2E7D32" : "#d32f2f" }}>
                    {data.gestionesHoy >= data.gestionesAyer ? "▲" : "▼"}{" "}
                    {Math.abs(data.gestionesHoy - data.gestionesAyer)} respecto a ayer
                  </span>
                }
              />
            </Grid>

            {/* 4. Compromisos Futuros (Pendientes y vigentes) */}
            <Grid item xs={12} sm={6} md={2.4}>
              <KPICard
                title="Compromisos Vigentes"
                value={data.compromisosFuturos}
                icon={<EventAvailable />}
                color="#ed6c02"
                subtitle="Aún no llega la fecha"
              />
            </Grid>

            {/* 5. Compromisos Vencidos */}
            <Grid item xs={12} sm={6} md={2.4}>
              <KPICard
                title="Compromisos Rotos"
                value={data.compromisosVencidos}
                icon={<EventBusy />}
                color="#d32f2f"
                subtitle="Fecha superó el día de hoy"
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 4 }} />

          {/* SECCIÓN DE GRÁFICOS COMPLEMENTARIOS */}
          <Grid container spacing={3}>
            {/* Gráfico de Compromisos */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{ p: 3, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column" }}
              >
                <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom align="center">
                  Estado de los Compromisos
                </Typography>
                <Box
                  sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}
                >
                  {data.compromisosFuturos === 0 && data.compromisosVencidos === 0 ? (
                    <Typography color="textSecondary">No existen compromisos registrados</Typography>
                  ) : (
                    <PieChart
                      series={[
                        {
                          data: pieData,
                          highlightScope: { faded: "global", highlighted: "item" },
                          faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
                          innerRadius: 60, // Para hacerlo estilo "Dona"
                          paddingAngle: 2,
                          cornerRadius: 4,
                        },
                      ]}
                      height={250}
                      slotProps={{
                        legend: { direction: "row", position: { vertical: "bottom", horizontal: "middle" } },
                      }}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Gráfico de Gestiones (Hoy vs Ayer) */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{ p: 3, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column" }}
              >
                <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom align="center">
                  Rendimiento de Gestión (Ayer vs Hoy)
                </Typography>
                <Box
                  sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}
                >
                  <BarChart
                    xAxis={[{ scaleType: "band", data: barData.map((d) => d.label) }]}
                    series={[
                      {
                        data: barData.map((d) => d.value),
                        color: "#196C87",
                        valueFormatter: (v) => `${v} gestiones`,
                      },
                    ]}
                    height={250}
                    margin={{ top: 20, bottom: 40, left: 40, right: 20 }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </StyledRoot>
      </div>
    </ThemeProvider>
  )
}

export default DashboardCarteraIndex
